export const PRICE_ORIGINS = [
  "l37_validated",
  "l37_provisional",
  "cype_fallback",
  "missing",
] as const;

export type PriceOrigin = (typeof PRICE_ORIGINS)[number];

export type L37PriceOffer = {
  resourceCode: string;
  unit: string;
  unitPrice: number;
  kind: "validated" | "provisional";
  articleSourceCode?: string | null;
  resourcePriceId?: string | null;
};

export type ResolvedUnitPrice = {
  priceOrigin: PriceOrigin;
  unitPrice: number | null;
  resourcePriceId: string | null;
  sourceCode: string | null;
  sourceUnit: string | null;
  sourceUnitPrice: number | null;
};

export type CoverageBucket = {
  origin: PriceOrigin;
  label: string;
  economicValue: number;
  sharePct: number | null;
};

const ORIGIN_LABEL: Record<PriceOrigin, string> = {
  l37_validated: "L37 validado",
  l37_provisional: "L37 provisório",
  cype_fallback: "CYPE fallback",
  missing: "Sem preço",
};

export function priceOriginLabel(origin: PriceOrigin) {
  return ORIGIN_LABEL[origin];
}

export function normalizePriceUnit(unit: string) {
  const key = unit.trim().toLowerCase().replaceAll("²", "2").replaceAll("³", "3").replaceAll(" ", "");
  if (key === "ud" || key === "un" || key === "unid" || key === "unidade") return "un";
  return key;
}

/** Half-up rounding that stays stable for decimal prices such as 2.025. */
export function roundHalfUp(value: number, digits = 2) {
  if (!Number.isFinite(value)) return Number.NaN;
  const factor = 10 ** digits;
  const scaled = value * factor;
  const rounded = Math.sign(scaled) * Math.floor(Math.abs(scaled) + 0.5 + 1e-8);
  return rounded / factor;
}

export function moneyCents(value: number) {
  return roundHalfUp(value, 2);
}

function usableOffer(offer: L37PriceOffer, resourceCode: string, unit: string, articleSourceCode?: string) {
  return offer.resourceCode === resourceCode
    && normalizePriceUnit(offer.unit) === normalizePriceUnit(unit)
    && (offer.articleSourceCode == null || offer.articleSourceCode === articleSourceCode)
    && Number.isFinite(offer.unitPrice)
    && offer.unitPrice >= 0
    && offer.unitPrice <= 1e9;
}

function pickOffer(offers: L37PriceOffer[], kind: L37PriceOffer["kind"], articleSourceCode?: string) {
  const matching = offers.filter((offer) => offer.kind === kind);
  return matching.find((offer) => offer.articleSourceCode != null && offer.articleSourceCode === articleSourceCode)
    ?? matching.find((offer) => offer.articleSourceCode == null)
    ?? null;
}

function fromOffer(offer: L37PriceOffer, origin: PriceOrigin): ResolvedUnitPrice {
  return {
    priceOrigin: origin,
    unitPrice: offer.unitPrice,
    resourcePriceId: offer.resourcePriceId ?? null,
    sourceCode: null,
    sourceUnit: null,
    sourceUnitPrice: null,
  };
}

const missingPrice: ResolvedUnitPrice = {
  priceOrigin: "missing",
  unitPrice: null,
  resourcePriceId: null,
  sourceCode: null,
  sourceUnit: null,
  sourceUnitPrice: null,
};

/**
 * Strict hierarchy: L37 validated, then L37 provisional/candidate, then a
 * technically compatible CYPE reference-line price, otherwise no price.
 * A CYPE fallback never receives an L37 resource price id.
 */
export function resolvePriceHierarchy(input: {
  resourceCode: string;
  unit: string;
  offers: L37PriceOffer[];
  articleSourceCode?: string;
  fallback?: { code: string; unit: string; unitPrice: number } | null;
  allowFallback?: boolean;
  forceMissing?: boolean;
}): ResolvedUnitPrice {
  if (input.forceMissing) return { ...missingPrice };
  const compatible = input.offers.filter((offer) =>
    usableOffer(offer, input.resourceCode, input.unit, input.articleSourceCode));
  const validated = pickOffer(compatible, "validated", input.articleSourceCode);
  if (validated) return fromOffer(validated, "l37_validated");
  const provisional = pickOffer(compatible, "provisional", input.articleSourceCode);
  if (provisional) return fromOffer(provisional, "l37_provisional");
  const fallback = input.fallback;
  if (input.allowFallback && fallback
    && normalizePriceUnit(fallback.unit) === normalizePriceUnit(input.unit)
    && Number.isFinite(fallback.unitPrice) && fallback.unitPrice >= 0) {
    return {
      priceOrigin: "cype_fallback",
      unitPrice: fallback.unitPrice,
      resourcePriceId: null,
      sourceCode: fallback.code,
      sourceUnit: fallback.unit,
      sourceUnitPrice: fallback.unitPrice,
    };
  }
  return { ...missingPrice };
}

export function coverageFromEconomicValues(
  values: Record<PriceOrigin, number>,
  dryUnitPrice: number | null,
): CoverageBucket[] {
  return PRICE_ORIGINS.map((origin) => {
    const economicValue = moneyCents(values[origin] ?? 0);
    const sharePct = dryUnitPrice !== null && dryUnitPrice > 0
      ? moneyCents(economicValue / dryUnitPrice * 100)
      : null;
    return { origin, label: priceOriginLabel(origin), economicValue, sharePct };
  });
}

function articleSourceFromRow(metadata: unknown) {
  if (metadata == null || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const raw = (metadata as { article_source_code?: unknown }).article_source_code;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

export function l37OfferFromPriceRow(row: {
  id?: string | null;
  resource_price_id?: string | null;
  code?: string | null;
  unit?: string | null;
  unit_price: number | string | null;
  status?: string | null;
  is_reference?: boolean | null;
  price_origin?: string | null;
  metadata?: unknown;
  resources?: { code: string; unit: string } | { code: string; unit: string }[] | null;
}): L37PriceOffer | null {
  const joined = Array.isArray(row.resources) ? row.resources[0] : row.resources;
  const resourceCode = joined?.code ?? row.code ?? "";
  const unit = joined?.unit ?? row.unit ?? "";
  const unitPrice = row.unit_price === null || row.unit_price === undefined ? null : Number(row.unit_price);
  if (!resourceCode || !unit || unitPrice === null || !Number.isFinite(unitPrice)) return null;
  const resourcePriceId = row.resource_price_id ?? row.id ?? null;
  const articleSourceCode = articleSourceFromRow(row.metadata);
  const validated = row.is_reference === true && row.status === "approved";
  const validatedView = row.status == null && row.is_reference == null && row.price_origin == null && row.resource_price_id != null;
  if (validated || validatedView || row.price_origin === "l37_validated") {
    return { resourceCode, unit, unitPrice, kind: "validated", resourcePriceId, articleSourceCode };
  }
  if (row.price_origin === "l37_provisional" && row.status === "candidate" && row.is_reference !== true) {
    return { resourceCode, unit, unitPrice, kind: "provisional", resourcePriceId, articleSourceCode };
  }
  return null;
}
